import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where, Timestamp, doc, getDoc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import AttendanceEdit from "./AttendanceEdit";
import { defaultSettings, summarizeAttendance } from "../utils/payroll";

function getDeviceId() {
  let id = localStorage.getItem("miyabi_device_id");
  if (!id) {
    id = "device_" + Math.random().toString(36).substr(2, 12) + "_" + Date.now();
    localStorage.setItem("miyabi_device_id", id);
  }
  return id;
}

function Attendance({ user, isAdmin, companyId, canViewAll, canManageDevices, userName = "" }) {
  const [deviceStatus, setDeviceStatus] = useState("checking");
  const [todayRecords, setTodayRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [punching, setPunching] = useState(false);
  const [message, setMessage] = useState("");
  const [lastType, setLastType] = useState(null);
  const [devices, setDevices] = useState([]);
  const [tab, setTab] = useState("punch");

  useEffect(() => {
    checkDeviceStatus();
    fetchTodayRecords();
    if (canViewAll || isAdmin) fetchAllRecords();
    if (canManageDevices || isAdmin) fetchDevices();
  }, []);

  const checkDeviceStatus = async () => {
    const deviceId = getDeviceId();
    try {
      // 会社に登録済み端末が1台もなければ制限なし（管理者がPC設定していない場合は自由打刻）
      if (companyId) {
        const allSnap = await getDocs(query(collection(db, "devices"), where("companyId", "==", companyId)));
        if (allSnap.empty) {
          setDeviceStatus("approved");
          return;
        }
      }

      // 登録済み端末がある場合はこのデバイスを確認
      const snap = await getDoc(doc(db, "devices", deviceId));
      if (!snap.exists()) {
        setDeviceStatus(isAdmin ? "admin_unregistered" : "unregistered");
      } else {
        const status = snap.data().status;
        if (status === "approved") {
          localStorage.setItem("miyabi_device_approved", "true");
          setDeviceStatus("approved");
        } else {
          setDeviceStatus("pending");
        }
      }
    } catch (e) {
      // 取得に失敗しても「確認中...」のまま固まらないようにする
      console.error("端末の確認に失敗しました:", e);
      setDeviceStatus(isAdmin ? "admin_unregistered" : "unregistered");
    }
  };

  const registerDevice = async () => {
    const deviceId = getDeviceId();
    const status = isAdmin ? "approved" : "pending";
    await setDoc(doc(db, "devices", deviceId), {
      deviceId,
      companyId: companyId || "",
      registeredBy: user.uid,
      status,
      createdAt: Timestamp.now(),
    });
    if (status === "approved") {
      localStorage.setItem("miyabi_device_approved", "true");
      localStorage.setItem("miyabi_device_companyId", companyId || "");
      // 会社名・ロゴをlocalStorageにキャッシュ（ログイン画面表示用）
      if (companyId) {
        try {
          // 会社の文書は会社コードそのものが名前なので、直接読む
          const snap = await getDoc(doc(db, "companies", companyId));
          if (snap.exists()) {
            const companyData = snap.data();
            localStorage.setItem("miyabi_company_name", companyData.name || "");
            localStorage.setItem("miyabi_company_logo", companyData.logo || "");
          }
        } catch {}
      }
    }
    setDeviceStatus(status);
    fetchDevices(); // 端末管理タブの表示も更新
  };

  const fetchTodayRecords = async () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const q = query(
      collection(db, "attendance"),
      where("uid", "==", user.uid),
      where("timestamp", ">=", Timestamp.fromDate(today))
    );
    const snap = await getDocs(q);
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(d => !d.cancelled)
      .sort((a, b) => b.timestamp - a.timestamp);
    setTodayRecords(data);
    if (data.length > 0) setLastType(data[0].type);
  };

  const fetchAllRecords = async () => {
    const today = new Date(); today.setHours(0, 0, 0, 0);

    // 通常の打刻（attendance）
    let q1;
    if (companyId) {
      q1 = query(collection(db, "attendance"), where("companyId", "==", companyId), where("timestamp", ">=", Timestamp.fromDate(today)));
    } else {
      q1 = query(collection(db, "attendance"), where("timestamp", ">=", Timestamp.fromDate(today)));
    }
    const snap1 = await getDocs(q1);
    const reg = snap1.docs.map(d => ({ id: d.id, source: "pc", ...d.data() }))
      .filter(d => !d.cancelled);

    // キオスク打刻（kioskAttendance）：phoneのみでクエリし日付はJS側でフィルタ
    let q2;
    if (companyId) {
      q2 = query(collection(db, "kioskAttendance"), where("companyId", "==", companyId));
    } else {
      q2 = collection(db, "kioskAttendance");
    }
    const snap2 = await getDocs(q2);
    const kiosk = snap2.docs
      .map(d => ({ id: d.id, source: "kiosk", ...d.data() }))
      .filter(d => d.timestamp?.toDate() >= today);

    const data = [...reg, ...kiosk].sort((a, b) => b.timestamp - a.timestamp);
    setAllRecords(data);
  };

  const fetchDevices = async () => {
    let q;
    if (companyId) {
      q = query(collection(db, "devices"), where("companyId", "==", companyId));
    } else {
      q = collection(db, "devices");
    }
    const snap = await getDocs(q);
    setDevices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handlePunch = async (type) => {
    setPunching(true);
    try {
      await addDoc(collection(db, "attendance"), {
        uid: user.uid,
        email: user.email,
        name: userName || user.email,
        companyId: companyId || "",
        type,
        timestamp: Timestamp.now(),
      });
      setLastType(type);
      setMessage(`${type}しました！`);
      setTimeout(() => setMessage(""), 3000);
      fetchTodayRecords();
      if (canViewAll || isAdmin) fetchAllRecords();
    } catch { setMessage("エラーが発生しました"); }
    setPunching(false);
  };

  const approveDevice = async (id) => {
    await updateDoc(doc(db, "devices", id), { status: "approved" });
    fetchDevices();
  };

  const deleteDevice = async (id) => {
    if (!window.confirm("この端末を削除しますか？")) return;
    await deleteDoc(doc(db, "devices", id));
    // 自分の端末IDと一致する場合はlocalStorageとステータスもリセット
    if (id === getDeviceId()) {
      localStorage.removeItem("miyabi_device_approved");
      localStorage.removeItem("miyabi_device_companyId");
      setDeviceStatus("admin_unregistered");
    }
    fetchDevices();
  };

  const fmt = (ts) => ts?.toDate().toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });

  const tabs = [
    { key: "punch",    label: "打刻" },
    { key: "mycsv",   label: "📥 自分の記録" },
    ...((canViewAll || isAdmin) ? [{ key: "all",    label: "全員の勤怠" }] : []),
    ...((canViewAll || isAdmin) ? [{ key: "fix",    label: "🛠 勤怠修正" }] : []),
    ...((isAdmin)               ? [{ key: "allcsv", label: "📊 全員CSV" }] : []),
    ...((canManageDevices || isAdmin) ? [{ key: "devices", label: "端末管理" }] : []),
  ];

  // CSV共通ダウンロード
  const downloadCSV = (rows, filename) => {
    const BOM = "﻿";
    const csv = BOM + rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // 指定ユーザーの打刻を期間取得してCSV
  const handleMyCSV = async (from, to, targetUid, targetName) => {
    const f = new Date(from); f.setHours(0,0,0,0);
    const t = new Date(to);   t.setHours(23,59,59,999);
    const q = query(collection(db, "attendance"),
      where("uid", "==", targetUid),
      where("timestamp", ">=", Timestamp.fromDate(f)),
      where("timestamp", "<=", Timestamp.fromDate(t))
    );
    const snap = await getDocs(q);
    const rows = [["氏名","日付","種別","時刻"]];
    snap.docs.map(d => d.data()).filter(r => !r.cancelled).sort((a,b)=>a.timestamp-b.timestamp).forEach(r => {
      const d = r.timestamp.toDate();
      rows.push([
        targetName,
        d.toLocaleDateString("ja-JP"),
        r.type,
        d.toLocaleTimeString("ja-JP",{hour:"2-digit",minute:"2-digit"})
      ]);
    });
    downloadCSV(rows, `勤怠記録_${targetName}_${from}_${to}.csv`);
  };

  // 選択した社員の期間集計CSV。
  // 休憩・残業・休日の判定は給与計算とまったく同じ処理を使う（数字が食い違わないように）。
  const handleAllCSV = async (from, to, targetUids, employeeMap) => {
    const f = new Date(from); f.setHours(0, 0, 0, 0);
    const t = new Date(to);   t.setHours(23, 59, 59, 999);

    // 会社の給与設定（休憩の自動控除・所定労働時間・法定休日）と会社カレンダーを読む
    let settings = defaultSettings(companyId);
    let calendar = [];
    try {
      const sSnap = await getDoc(doc(db, "payrollSettings", companyId));
      if (sSnap.exists()) settings = { ...settings, ...sSnap.data() };
    } catch (e) {
      console.warn("給与設定を読めなかったため既定値で集計します:", e);
    }
    try {
      const cSnap = await getDoc(doc(db, "companyCalendars", companyId));
      if (cSnap.exists()) calendar = cSnap.data().days || [];
    } catch (e) {
      console.warn("会社カレンダーを読めなかったため曜日の設定で判定します:", e);
    }

    const q = query(collection(db, "attendance"),
      where("companyId", "==", companyId),
      where("timestamp", ">=", Timestamp.fromDate(f)),
      where("timestamp", "<=", Timestamp.fromDate(t))
    );
    const snap = await getDocs(q);
    const records = snap.docs.map(d => d.data()).filter(r => !r.cancelled);

    const byUser = {};
    records.forEach(r => { (byUser[r.uid] = byUser[r.uid] || []).push(r); });

    const hh = (min) => (min / 60).toFixed(2);
    const rows = [[
      "社員ID", "氏名", "出勤日数", "労働時間", "休憩時間",
      "所定内時間", "法定内残業", "法定外残業", "深夜時間",
      "休日出勤日数", "休日出勤時間", "打刻漏れ日数",
    ]];

    // 選択が空なら、打刻のあった社員すべてを対象にする
    const uids = (targetUids && targetUids.length > 0) ? targetUids : Object.keys(byUser);

    uids.forEach(uid => {
      const emp = (employeeMap && employeeMap[uid]) || {};
      const punches = (byUser[uid] || [])
        .filter(r => r.timestamp?.toDate)
        .map(r => ({ type: r.type, timestamp: r.timestamp.toDate() }));

      const sum = summarizeAttendance(punches, settings, calendar);
      const done = sum.days.filter(d => !d.incomplete);
      const workMin  = done.reduce((a, d) => a + (d.minutes || 0), 0);
      const breakMin = done.reduce((a, d) => a + (d.breakMin || 0), 0);
      const holidayDays = done.filter(d => d.isLegalHoliday).length;

      rows.push([
        emp.memberNo || "",
        emp.name || emp.email || uid,
        sum.workDays,
        hh(workMin),
        hh(breakMin),
        hh(sum.plainMin),
        hh(sum.withinOtMin),
        hh(sum.legalOtMin),
        hh(sum.nightMin),
        holidayDays,
        hh(sum.holidayMin),
        sum.incompleteDays.length,
      ]);
    });

    const label = (targetUids && targetUids.length === 1 && employeeMap[targetUids[0]])
      ? (employeeMap[targetUids[0]].name || "社員")
      : "全員";
    downloadCSV(rows, `勤怠集計_${label}_${from}_${to}.csv`);
  };

  return (
    <div style={st.container}>
      <h2 style={st.title}>🕐 出退勤</h2>
      <div style={st.tabs}>
        {tabs.map(t => (
          <button key={t.key} style={{ ...st.tab, ...(tab === t.key ? st.activeTab : {}) }} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === "punch" && (
        <>
          {deviceStatus === "admin_unregistered" && (
            <div style={st.card}>
              <p style={{ color: "#27ae60", marginBottom: "12px" }}>✅ 管理者として打刻できます。ログイン画面にQRを表示するには設定 → PC設定から端末登録してください。</p>
              <button style={{ ...st.approveBtn, backgroundColor: "#2c3e50" }} onClick={() => setDeviceStatus("approved")}>打刻する</button>
            </div>
          )}
          {deviceStatus === "unregistered" && (
            <div style={st.card}>
              <p style={{ color: "#e67e22", fontSize: "15px", marginBottom: "8px" }}>🏢 打刻は会社のQRコードまたは会社の登録済みPCからのみ行えます。</p>
              <p style={{ color: "#7f8c8d", fontSize: "13px" }}>このデバイスは打刻端末として登録されていません。<br/>会社内のPCで管理者に端末登録してもらうか、会社に設置されたQRコードをスマホで読み取って打刻してください。</p>
            </div>
          )}
          {deviceStatus === "pending" && <div style={st.card}><p>⏳ 端末の承認待ちです。管理者に承認を依頼してください。</p></div>}
          {deviceStatus === "approved" && (
            <div style={st.card}>
              {message && <div style={st.msg}>{message}</div>}
              <div style={st.btnRow}>
                <button
                  style={{ ...st.punchBtn, backgroundColor: lastType === "出勤" ? "#95a5a6" : "#27ae60" }}
                  onClick={() => handlePunch("出勤")}
                  disabled={punching || lastType === "出勤"}
                >出勤</button>
                <button
                  style={{ ...st.punchBtn, backgroundColor: lastType !== "出勤" ? "#95a5a6" : "#e74c3c" }}
                  onClick={() => handlePunch("退勤")}
                  disabled={punching || lastType !== "出勤"}
                >退勤</button>
              </div>
              {todayRecords.length > 0 && (
                <div style={st.records}>
                  <p style={st.recTitle}>本日の打刻</p>
                  {todayRecords.map(r => (
                    <div key={r.id} style={st.recRow}>
                      <span style={{ color: r.type === "出勤" ? "#27ae60" : "#e74c3c", fontWeight: "bold" }}>{r.type}</span>
                      <span style={{ color: "#999" }}>{fmt(r.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {deviceStatus === "checking" && <p>確認中...</p>}
        </>
      )}

      {tab === "all" && (
        <div style={st.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h3 style={{ color: "#2c3e50", margin: 0 }}>本日の打刻一覧</h3>
            <button style={{ padding: "6px 16px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }} onClick={fetchAllRecords}>🔄 更新</button>
          </div>
          {allRecords.length === 0 ? <p style={{ color: "#999" }}>本日の打刻はありません</p> : (
            <table style={st.table}>
              <thead><tr><th style={st.th}>名前</th><th style={st.th}>種別</th><th style={st.th}>時刻</th><th style={st.th}>方法</th></tr></thead>
              <tbody>
                {allRecords.map(r => (
                  <tr key={r.id}>
                    <td style={st.td}>{r.name || r.email || "-"}</td>
                    <td style={{ ...st.td, color: r.type === "出勤" ? "#27ae60" : "#e74c3c", fontWeight: "bold" }}>{r.type}</td>
                    <td style={st.td}>{fmt(r.timestamp)}</td>
                    <td style={{ ...st.td, fontSize: "12px", color: "#999" }}>{r.source === "kiosk" ? "📱QR" : "💻PC"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "fix" && (canViewAll || isAdmin) && (
        <AttendanceEdit user={user} companyId={companyId} />
      )}

      {(tab === "mycsv" || tab === "allcsv") && (
        <CsvDownloadTab
          tab={tab}
          onMyCSV={handleMyCSV}
          onAllCSV={handleAllCSV}
          currentUser={{ uid: user.uid, name: userName || user.email }}
          isAdmin={isAdmin}
          companyId={companyId}
        />
      )}

      {tab === "devices" && (
        <div style={st.card}>
          <h3 style={{ color: "#2c3e50", marginBottom: "16px" }}>打刻端末管理</h3>
          {devices.filter(d => d.id === getDeviceId()).length === 0 && (
            <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f0f4f8", borderRadius: "8px" }}>
              <p style={{ marginBottom: "8px", fontSize: "14px", color: "#555" }}>このPCは打刻端末として未登録です。</p>
              <button style={st.approveBtn} onClick={async () => { await registerDevice(); fetchDevices(); }}>この端末を打刻端末として登録する</button>
            </div>
          )}
          {devices.length === 0 ? <p style={{ color: "#999" }}>登録された端末はありません</p> : devices.map(d => (
            <div key={d.id} style={st.deviceRow}>
              <div>
                <div style={{ fontSize: "13px", color: "#2c3e50" }}>{d.deviceId}</div>
                <div style={{ fontSize: "12px", color: "#999" }}>状態：{d.status === "approved" ? "✅ 承認済み" : "⏳ 承認待ち"}</div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {d.status !== "approved" && <button style={st.approveBtn} onClick={() => approveDevice(d.id)}>承認</button>}
                <button style={st.deleteBtn} onClick={() => deleteDevice(d.id)}>削除</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "800px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "24px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px" },
  tab: { padding: "10px 20px", border: "2px solid #ddd", borderRadius: "8px", backgroundColor: "#fff", cursor: "pointer", fontSize: "14px", color: "#7f8c8d" },
  activeTab: { border: "2px solid #2c3e50", color: "#2c3e50", fontWeight: "bold" },
  card: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px" },
  msg: { backgroundColor: "#d5f5e3", color: "#27ae60", padding: "12px", borderRadius: "8px", marginBottom: "16px", textAlign: "center", fontWeight: "bold" },
  btnRow: { display: "flex", gap: "16px", marginBottom: "24px" },
  punchBtn: { flex: 1, padding: "28px 0", fontSize: "22px", fontWeight: "bold", color: "#fff", border: "none", borderRadius: "12px", cursor: "pointer" },
  records: { borderTop: "1px solid #eee", paddingTop: "16px" },
  recTitle: { color: "#999", fontSize: "13px", marginBottom: "8px" },
  recRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: "15px" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { backgroundColor: "#2c3e50", color: "#fff", padding: "10px", textAlign: "left", fontSize: "14px" },
  td: { padding: "10px", borderBottom: "1px solid #eee", fontSize: "14px" },
  deviceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #eee" },
  approveBtn: { padding: "8px 16px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  deleteBtn: { padding: "8px 16px", backgroundColor: "#e74c3c", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
};

function CsvDownloadTab({ tab, onMyCSV, onAllCSV, currentUser, isAdmin, companyId }) {
  const today = new Date().toISOString().slice(0,10);
  const firstDay = today.slice(0,8) + "01";
  const [from, setFrom] = useState(firstDay);
  const [to,   setTo]   = useState(today);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedUid, setSelectedUid] = useState(currentUser.uid);
  const [selectedName, setSelectedName] = useState(currentUser.name);
  const [checked, setChecked] = useState({});   // 集計CSVで選んだ社員

  useEffect(() => {
    if (!companyId) return;
    if (!(isAdmin || tab === "allcsv")) return;
    getDocs(query(collection(db, "users"), where("companyId", "==", companyId), where("status", "==", "approved")))
      .then(snap => {
        const list = snap.docs.map(d => ({
          uid: d.id,
          name: d.data().name || d.data().email,
          email: d.data().email || "",
          memberNo: d.data().memberNo || "",
          nameKana: d.data().nameKana || "",
        })).sort((a, b) => (a.nameKana || a.name).localeCompare(b.nameKana || b.name, "ja"));
        setEmployees(list);
      })
      .catch(e => console.error("社員一覧の取得に失敗しました:", e));
  }, [tab, isAdmin, companyId]);

  const selectedUids = employees.filter(e => checked[e.uid]).map(e => e.uid);
  const allChecked = employees.length > 0 && employees.every(e => checked[e.uid]);
  const toggleAll = () => {
    if (allChecked) setChecked({});
    else { const n = {}; employees.forEach(e => { n[e.uid] = true; }); setChecked(n); }
  };

  const handle = async () => {
    if (!from || !to || from > to) { alert("期間を正しく設定してください"); return; }
    setLoading(true); setDone(false);
    if (tab === "mycsv") {
      await onMyCSV(from, to, selectedUid, selectedName);
    } else {
      const map = {};
      employees.forEach(e => { map[e.uid] = e; });
      await onAllCSV(from, to, selectedUids, map);
    }
    setLoading(false); setDone(true);
    setTimeout(() => setDone(false), 3000);
  };

  return (
    <div style={stc.card}>
      <h3 style={stc.title}>
        {tab === "mycsv" ? "📥 勤怠記録ダウンロード" : "📊 勤怠集計をダウンロード"}
      </h3>
      <p style={stc.desc}>
        {tab === "mycsv"
          ? "指定期間内の出勤・退勤記録をCSVで出力します。"
          : "選択した社員の出勤日数・労働時間・休憩・所定内・法定内残業・法定外残業・深夜・休日出勤をCSVで出力します。休憩と残業は給与設定の所定労働時間にもとづいて計算します。"}
      </p>

      {/* 管理者の場合は社員選択 */}
      {tab === "allcsv" && employees.length > 0 && (
        <div style={stc.pickBox}>
          <div style={stc.pickHead}>
            <label style={stc.pickAll}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} style={stc.pickCb} />
              全員を選択
            </label>
            <span style={stc.pickCount}>
              {selectedUids.length > 0 ? `${selectedUids.length}名を選択中` : "未選択のときは全員が対象になります"}
            </span>
          </div>
          <div style={stc.pickList}>
            {employees.map(e => (
              <label key={e.uid} style={stc.pickItem}>
                <input type="checkbox" style={stc.pickCb}
                  checked={!!checked[e.uid]}
                  onChange={ev => setChecked({ ...checked, [e.uid]: ev.target.checked })} />
                {e.name}{e.memberNo ? <span style={stc.pickNo}>{e.memberNo}</span> : null}
              </label>
            ))}
          </div>
        </div>
      )}

      {tab === "mycsv" && isAdmin && employees.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <label style={stc.label}>社員を選択</label>
          <select style={{ ...stc.input, width: "auto", minWidth: "200px" }}
            value={selectedUid}
            onChange={e => {
              const emp = employees.find(em => em.uid === e.target.value);
              setSelectedUid(e.target.value);
              setSelectedName(emp?.name || e.target.value);
            }}>
            {employees.map(emp => (
              <option key={emp.uid} value={emp.uid}>{emp.name}</option>
            ))}
          </select>
        </div>
      )}

      <div style={stc.row}>
        <div style={stc.field}>
          <label style={stc.label}>開始日</label>
          <input type="date" style={stc.input} value={from} onChange={e=>setFrom(e.target.value)} />
        </div>
        <span style={{ paddingTop: "32px", color: "#999" }}>〜</span>
        <div style={stc.field}>
          <label style={stc.label}>終了日</label>
          <input type="date" style={stc.input} value={to} onChange={e=>setTo(e.target.value)} />
        </div>
      </div>
      <button style={stc.btn} onClick={handle} disabled={loading}>
        {loading ? "取得中..." : "📥 CSVをダウンロード"}
      </button>
      {done && <span style={{ marginLeft: "12px", color: "#27ae60", fontSize: "14px" }}>✅ ダウンロードしました</span>}
    </div>
  );
}

const stc = {
  card: { backgroundColor: "#fff", padding: "28px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px" },
  title: { color: "#2c3e50", marginBottom: "8px" },
  desc: { color: "#7f8c8d", fontSize: "14px", marginBottom: "24px" },
  row: { display: "flex", gap: "16px", alignItems: "flex-start", marginBottom: "20px", flexWrap: "wrap" },
  field: { display: "flex", flexDirection: "column" },
  label: { fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  input: { padding: "10px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px" },
  btn: { padding: "12px 28px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
  pickBox: { border: "1px solid #e6ebf0", borderRadius: "10px", padding: "12px 14px", marginBottom: "20px", backgroundColor: "#fafbfc" },
  pickHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px" },
  pickAll: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", fontWeight: "bold", cursor: "pointer" },
  pickCount: { fontSize: "12px", color: "#7f8c8d" },
  pickList: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "4px", maxHeight: "240px", overflowY: "auto" },
  pickItem: { display: "flex", alignItems: "center", fontSize: "14px", color: "#2c3e50", cursor: "pointer", padding: "4px 0" },
  pickCb: { width: "16px", height: "16px", marginRight: "8px" },
  pickNo: { marginLeft: "8px", fontSize: "11px", color: "#2980b9", fontFamily: "monospace" },
};

export default Attendance;
